// Wound Care (Lumina Lens) — thin wrappers over the FastAPI proxy.
// Every path forwards the tenant JWT via the shared axios instance and lands on
// upstream /api/edi/televisit/wound-analysis*. Wound Care is a subset of the
// dictation workflow — always pass dictation_session_id so results bind to the
// session (session badge + automatic WOUND EVENT tag at claim time).
import { api } from "@/lib/api";

export const WOUND_ENGINES = [
  { value: "extreme", label: "Lumina Lens Extreme", model: "GPT-6 Astra" },
  { value: "premium", label: "Lumina Lens Premium", model: "Claude Opus 5" },
  { value: "upgraded", label: "Lumina Lens Upgraded", model: "Claude Sonnet 5" },
  { value: "basic", label: "Lumina Lens Basic", model: "GPT-5.6 Sol" },
];

// Facts-only prefill from the accumulated transcript. Returns {wound_site, context}
// (empty strings when nothing about a wound was dictated).
export async function deriveWoundContext(transcript) {
  const { data } = await api.post("/api/edi/televisit/wound-analysis/derive-context", { transcript });
  return data || { wound_site: "", context: "" };
}

// Submit up to 4 photos of the SAME wound for async analysis → { job_id, status }.
// Always use /async + poll from the browser (sync variant can exceed the CDN cap).
export async function submitWoundAnalysis(body) {
  const { data } = await api.post("/api/edi/televisit/wound-analysis/async", body);
  return data;
}

export async function getWoundJob(jobId) {
  const { data } = await api.get(`/api/edi/televisit/wound-analysis/job/${jobId}`);
  return data;
}

// Analyses tied to a dictation session (newest first) — powers the "N captured" badge.
export async function listSessionWounds(sessionId) {
  const { data } = await api.get("/api/edi/televisit/wound-analysis", {
    params: { dictation_session_id: sessionId, limit: 25 },
  });
  return Array.isArray(data?.analyses) ? data.analyses : [];
}

// Cross-visit progression grouped by wound (patient_mrn + normalized wound_key).
export async function woundTimeline(mrn, chart_uuid, client_id) {
  // DQ-14: the endpoint now REQUIRES a patient scope — a call with none of
  // chart_uuid / mrn / client_id returns 422 (previously leaked every patient
  // tenant-wide). Prefer chart_uuid (charts often have no/mismatched MRN).
  const params = {};
  if (chart_uuid) params.chart_uuid = chart_uuid;
  if (mrn) params.mrn = mrn;
  if (client_id) params.client_id = client_id;
  if (!params.chart_uuid && !params.mrn && !params.client_id) return []; // never fire an unscoped (422) call
  try {
    const { data } = await api.get("/api/edi/televisit/wound-analysis/timeline", { params });
    return Array.isArray(data?.groups) ? data.groups : [];
  } catch (e) {
    if (e?.response?.status === 422) return []; // unscoped/invalid scope → treat as no timeline
    throw e;
  }
}

// Read a file into base64 (no data: prefix), downscaling large phone photos so the
// JSON payload stays well under proxy/body limits. Keeps aspect ratio.
export function fileToBase64(file, maxDim = 1600, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const scale = maxDim / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      const dataUrl = canvas.toDataURL("image/jpeg", quality);
      resolve({ base64: dataUrl.split(",")[1], preview: dataUrl });
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Couldn't read that image.")); };
    img.src = url;
  });
}

// Poll a job to completion. Resolves with the analysis result doc, rejects on error.
export function pollWoundJob(jobId, { interval = 2500, tries = 40, onTick } = {}) {
  return new Promise((resolve, reject) => {
    let n = 0;
    const tick = async () => {
      try {
        const d = await getWoundJob(jobId);
        const st = (d?.status || "").toLowerCase();
        onTick?.(st, n);
        if (st === "done") return resolve(d.result);
        if (st === "error") return reject(new Error(d?.error || "Analysis failed."));
        if (++n > tries) return reject(new Error("Timed out waiting for the analysis."));
        setTimeout(tick, interval);
      } catch (e) { reject(e); }
    };
    setTimeout(tick, interval);
  });
}
